// ============================================================
// systems/win.js — Win system, badge management, game reset
// ============================================================
import { state } from '../state.js';
import { dom } from '../dom.js';
import { BADGE_CHARACTERS } from '../constants.js';
import { updateShopUI } from './shop.js';
import { setCharacterImage } from './menu.js';
import { saveGame } from './save.js';
import { playVictoryFanfare } from '../audio/sfx.js';
import { startCurrentMusic, fadeOutAllMusic, pauseMusic, stopAllMusic } from '../audio/manager.js';
import { stopMusic } from '../audio/musicChill.js';
import { stopOgMusic } from '../audio/musicOg.js';
import { stopVoidMusic } from '../audio/musicVoid.js';
import { stopPhase5Music } from '../audio/musicPhase5.js';
import { isLoggedIn, showAuthModal } from './auth.js';
import { supabase } from './supabase.js';

// Forward-declared functions — set via provideWinDeps() to avoid circular deps.
let deactivatePhase5;
let deactivateEyeBoss;
let resetEyeBossTrigger;
let deactivatePhase4;
let deactivatePhase3;
let stopFireEffect;
let resetLives;
let showLivesUI;
let stopShieldSpawning;
let startShieldSpawning;
let goToMenu;

/**
 * Provide external functions that win.js needs to call.
 * Called once at bootstrap to break circular dependency chains.
 */
export function provideWinDeps(deps) {
  deactivatePhase5     = deps.deactivatePhase5;
  deactivateEyeBoss    = deps.deactivateEyeBoss;
  resetEyeBossTrigger  = deps.resetEyeBossTrigger;
  deactivatePhase4     = deps.deactivatePhase4;
  deactivatePhase3     = deps.deactivatePhase3;
  stopFireEffect       = deps.stopFireEffect;
  resetLives           = deps.resetLives;
  showLivesUI          = deps.showLivesUI;
  stopShieldSpawning   = deps.stopShieldSpawning;
  startShieldSpawning  = deps.startShieldSpawning;
  goToMenu             = deps.goToMenu;
}

// ---- Badges UI ----

export function updateBadgesUI() {
  dom.badgeGrid.innerHTML = '';
  BADGE_CHARACTERS.forEach(c => {
    const unlocked = state.badges[c.id] === true;
    const card = document.createElement('div');
    card.className = 'badge-card ' + (unlocked ? 'unlocked' : 'locked');
    card.innerHTML =
      '<img class="badge-img" src="' + c.img + '" alt="' + c.name + '">' +
      '<div class="badge-info">' +
        '<div class="badge-char-name">' + c.name + '</div>' +
        '<div class="badge-status">' + (unlocked ? 'Escaped!' : 'Locked') + '</div>' +
      '</div>' +
      '<div class="badge-icon">' + (unlocked ? '&#x2705;' : '&#x1F512;') + '</div>';
    dom.badgeGrid.appendChild(card);
  });

  // "???" badge — escape with ALL PLUSHIES (reward: unlocks Waine)
  const allUnlocked = BADGE_CHARACTERS.every(c => state.badges[c.id] === true);
  const masterCard = document.createElement('div');
  masterCard.className = 'badge-card master-badge mystery-badge ' + (allUnlocked ? 'unlocked' : 'locked');
  masterCard.innerHTML =
    '<div class="badge-icon" style="font-size:36px;">' + (allUnlocked ? '&#x1F3AA;' : '&#x2753;') + '</div>' +
    '<div class="badge-info">' +
      '<div class="badge-char-name">' + (allUnlocked ? 'Digital Circus Master' : '???') + '</div>' +
      '<div class="badge-status">' + (allUnlocked
        ? 'All plushies escaped! Reward: Waine unlocked \uD83C\uDF81'
        : 'Escape with ALL PLUSHIES to get this badge and a reward')
        + '</div>' +
    '</div>' +
    '<div class="badge-icon">' + (allUnlocked ? '&#x1F451;' : '&#x1F512;') + '</div>';
  dom.badgeGrid.appendChild(masterCard);

  // Secret character badge — escape with ELCHICOQUESEPARECEMUCHOATOMNI
  const secretEscaped = state.badges.secret === true;
  const secretCard = document.createElement('div');
  secretCard.className = 'badge-card secret-badge ' + (secretEscaped ? 'unlocked' : 'locked');
  secretCard.innerHTML =
    '<img class="badge-img" src="/images/ELCHICOQUESEPARECEMUCHOATOMNI.png" alt="???"' +
    (secretEscaped ? '' : ' style="filter:brightness(0) saturate(0);"') + '>' +
    '<div class="badge-info">' +
      '<div class="badge-char-name">' + (secretEscaped ? 'ELCHICOQUESEPARECEMUCHOATOMNI' : '???') + '</div>' +
      '<div class="badge-status">' + (secretEscaped
        ? 'Escaped with the mysterious friend!'
        : 'Escape with the secret character')
        + '</div>' +
    '</div>' +
    '<div class="badge-icon">' + (secretEscaped ? '&#x2728;' : '&#x1F512;') + '</div>';
  dom.badgeGrid.appendChild(secretCard);
}

// ---- Win trigger ----

export function triggerWin() {
  if (state.escaped) return;
  state.escaped = true;

  // Unlock badge for current character
  state.badges[state.selectedCharacter] = true;

  // Check if all plushies escaped — unlock Waine
  const allEscaped = BADGE_CHARACTERS.every(c => state.badges[c.id] === true);
  if (allEscaped && !state.caineUnlocked) {
    state.caineUnlocked = true;
    if (dom.charWaineSelect) {
      dom.charWaineSelect.classList.remove('locked');
      const lock = dom.charWaineSelect.querySelector('.char-lock');
      if (lock) lock.style.display = 'none';
    }
  }

  // Secret character — unlocks only after you also escape with Waine
  if (allEscaped && state.badges.waine === true && !state.secretUnlocked) {
    state.secretUnlocked = true;
    if (dom.charSecretSelect) {
      dom.charSecretSelect.classList.remove('locked');
      const lock = dom.charSecretSelect.querySelector('.char-lock');
      if (lock) lock.style.display = 'none';
      // Reveal its real name now
      const name = dom.charSecretSelect.querySelector('.char-name');
      if (name) name.textContent = 'ELCHICOQUESEPARECEMUCHOATOMNI';
      const img = dom.charSecretSelect.querySelector('img');
      if (img) img.alt = 'ELCHICOQUESEPARECEMUCHOATOMNI';
    }
  }

  saveGame();

  // Upload score to leaderboard if logged in
  if (state.onlineMode && state.supabaseUser) {
    if (state.score > state.highScore) {
      state.highScore = state.score;
      localStorage.setItem('smoothClicker_highScore', state.highScore.toString());
    }
    const meta = state.supabaseUser.user_metadata || {};
    const emailPrefix = state.supabaseUser.email ? state.supabaseUser.email.split('@')[0] : 'Player';
    const displayName =
      state.userProfile?.display_name ||
      state.userProfile?.username ||
      meta.display_name || meta.full_name || meta.name || meta.user_name ||
      emailPrefix; // never the raw email
    supabase.from('leaderboard').upsert({
      id: state.supabaseUser.id,
      display_name: displayName,
      high_score: state.highScore,
      updated_at: new Date().toISOString(),
    }).then(({ error }) => {
      if (error) console.warn('[win] leaderboard upsert error:', error.message);
    }, (err) => console.warn('[win] leaderboard upsert threw:', err?.message || err));
  }

  // Stop all music
  stopPhase5Music();
  stopVoidMusic();
  dom.phase3Audio.pause();
  stopMusic();
  stopOgMusic();

  // Clear all hazard intervals
  clearInterval(state.bombInterval);
  cancelAnimationFrame(state.bombAnimFrame);
  clearInterval(state.lightningInterval);
  clearInterval(state.iceInterval);
  clearInterval(state.icebergInterval);
  cancelAnimationFrame(state.icebergAnimFrame);
  state.autoClickerIntervals.forEach(id => clearInterval(id));

  // Stop phase 3 attacks
  clearInterval(state.absAttackInterval);
  clearInterval(state.absLaserInterval);
  clearInterval(state.absShockwaveInterval);
  clearInterval(state.absGlitchBombInterval);
  clearInterval(state.absGlitchHazardInterval);
  cancelAnimationFrame(state.absGlitchBombFrame);

  // Remove all hazard elements from screen
  state.bombs.forEach(b => b.el.remove());
  state.bombs = [];
  state.icebergs.forEach(b => b.el.remove());
  state.icebergs = [];
  state.absGlitchBombs.forEach(b => b.el.remove());
  state.absGlitchBombs = [];
  document.querySelectorAll('.bomb, .iceberg, .glitch-bomb, .lightning-bolt, .lightning-warn, .glitch-laser, .abs-attack-warn, .abs-attack-line, .abs-shockwave, .abs-creature').forEach(el => el.remove());

  // Stop game
  dom.smiley.style.pointerEvents = 'none';

  // Show win screen
  dom.winScoreEl.textContent = state.score;
  dom.winScreen.classList.add('show');

  // Play victory fanfare
  playVictoryFanfare();

  // Spawn fireworks
  function spawnFirework() {
    if (!state.escaped) return;
    const colors = ['#ff1744','#ff6d00','#ffea00','#76ff03','#00e5ff','#d500f9','#651fff'];
    const x = Math.random() * window.innerWidth;
    const y = Math.random() * window.innerHeight * 0.6;
    for (let i = 0; i < 12; i++) {
      const spark = document.createElement('div');
      spark.className = 'win-firework';
      spark.style.left = x + 'px';
      spark.style.top = y + 'px';
      spark.style.background = colors[Math.floor(Math.random() * colors.length)];
      const angle = (Math.PI * 2 / 12) * i;
      const dist = 40 + Math.random() * 60;
      spark.style.animation = 'none';
      spark.style.transform = 'translate(0,0)';
      spark.style.opacity = '1';
      spark.style.transition = 'transform 0.8s ease-out, opacity 0.8s ease-out';
      document.body.appendChild(spark);
      requestAnimationFrame(() => {
        spark.style.transform = `translate(${Math.cos(angle)*dist}px, ${Math.sin(angle)*dist}px)`;
        spark.style.opacity = '0';
      });
      setTimeout(() => spark.remove(), 1000);
    }
  }
  let fwCount = 0;
  const fwInterval = setInterval(() => {
    spawnFirework();
    fwCount++;
    if (fwCount > 30) clearInterval(fwInterval);
  }, 300);

  // Prompt to create account if not logged in (after a short delay)
  if (!isLoggedIn()) {
    setTimeout(() => {
      showAuthModal('Create an account to save your badge and climb the leaderboard!');
    }, 2000);
  }
}

/**
 * Clear all active hazards and stop spawners WITHOUT resetting shop/score.
 * Used on Save & Menu so nothing keeps spawning/animating while on menu
 * or while the game is frozen waiting for first click.
 */
export function clearHazards() {
  // Bombs
  state.bombs.forEach(b => b.el.remove());
  state.bombs = [];
  clearInterval(state.bombInterval);
  state.bombInterval = null;
  cancelAnimationFrame(state.bombAnimFrame);
  state.bombAnimFrame = null;

  // Icebergs / lightning / ice
  clearInterval(state.lightningInterval);
  state.lightningInterval = null;
  clearInterval(state.iceInterval);
  state.iceInterval = null;
  clearInterval(state.icebergInterval);
  state.icebergInterval = null;
  cancelAnimationFrame(state.icebergAnimFrame);
  state.icebergAnimFrame = null;
  state.icebergs.forEach(b => b.el.remove());
  state.icebergs = [];

  // Eye boss (if mid-encounter)
  deactivateEyeBoss();

  // Auto-clickers stop grinding while on menu / frozen
  state.autoClickerIntervals.forEach(id => clearInterval(id));
  state.autoClickerIntervals = [];
  state.autoMouseElements.forEach(m => m.el.remove());
  state.autoMouseElements = [];

  // Reset visual phase flags that indicate active hazards
  state.bombMode = false;
  state.frozenMode = false;
  document.body.classList.remove('danger-mode');
  document.body.classList.remove('frozen-mode');

  // Stop fire effect
  stopFireEffect();
}

// ---- Reset game ----

export function resetGame() {
  state.dead = false;
  state.bombMode = false;
  state.darkMusicPlaying = false;
  state.escaped = false;
  resetLives();
  stopShieldSpawning();
  if (state.gameMode === 'competitive') { showLivesUI(true); startShieldSpawning(); }
  state.paused = false;
  state.score = 0;
  state.combo = 0;
  dom.scoreEl.textContent = '0';
  dom.comboEl.classList.remove('show');
  dom.gameOverEl.classList.remove('show');
  dom.winScreen.classList.remove('show');
  dom.charSelect.classList.remove('show');
  dom.pauseOverlay.classList.remove('show');
  dom.pauseBtn.innerHTML = '&#x23F8;';
  dom.smiley.style.pointerEvents = '';
  dom.smiley.classList.remove('burning');
  dom.smiley.classList.remove('frozen');
  dom.smiley.classList.remove('escaping');
  dom.smiley.classList.remove('abstracted');
  dom.smiley.classList.remove('greyed');
  stopFireEffect();
  deactivatePhase5();
  deactivatePhase4();
  deactivatePhase3();
  deactivateEyeBoss();
  resetEyeBossTrigger();
  state.frozenMode = false;
  clearInterval(state.lightningInterval);
  clearInterval(state.iceInterval);
  clearInterval(state.icebergInterval);
  cancelAnimationFrame(state.icebergAnimFrame);
  state.icebergs.forEach(b => b.el.remove());
  state.icebergs = [];
  document.body.classList.remove('danger-mode');
  document.body.classList.remove('frozen-mode');

  // Reset shop
  state.clickPower = 1;
  state.clickPowerCost = 50;
  state.clickPowerLevel = 0;
  state.autoClickerCount = 0;
  state.autoClickerCost = 250;
  state.autoSpeedLevel = 0;
  state.autoSpeedCost = 500;
  state.megaClickLevel = 0;
  state.megaClickCost = 1000;
  state.autoClickerIntervals.forEach(id => clearInterval(id));
  state.autoClickerIntervals = [];
  state.autoMouseElements.forEach(m => m.el.remove());
  state.autoMouseElements = [];
  updateShopUI();
  dom.shopEl.classList.remove('open');

  // Clear all bombs
  state.bombs.forEach(b => b.el.remove());
  state.bombs = [];
  clearInterval(state.bombInterval);
  cancelAnimationFrame(state.bombAnimFrame);

  // Restart music if it was playing
  if (state.musicPlaying || state.darkMusicPlaying) {
    state.darkMusicPlaying = false;
    stopOgMusic();
    state.musicNodes.forEach(n => { try { n.disconnect(); } catch(e){} });
    state.musicNodes = [];
    state.musicPlaying = false;
    startCurrentMusic();
  }

  // Set character image last, after all phases are deactivated
  setCharacterImage(state.selectedCharacter);
}

// ---- Win screen button handlers ----

export function initWinHandlers() {
  // Retry button (competitive game over)
  dom.retryBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    resetGame();
  });

  dom.winNewCharBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    state.charSelectFromWin = true;
    dom.winScreen.classList.remove('show');
    dom.charSelect.classList.add('show');
  });

  dom.winMenuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    state.escaped = false;
    deactivatePhase5();
    // Smooth pause-fade, matching the pause button feel, then go to menu
    pauseMusic(600);
    setTimeout(() => {
      stopAllMusic();
      resetGame();
      goToMenu();
    }, 650);
  });
}
